import { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Pencil, Check, X, Ban, CheckCircle2 } from 'lucide-react';
import { ColumnDef, DraggableTh, useColumnPrefs } from '../components/ColumnPicker';
import { useAuth } from '../context/AuthContext';

interface Partner {
  id: number;
  name: string;
  slug: string;
  contact_email: string | null;
  active: boolean;
}

const PARTNER_COLUMNS: ColumnDef[] = [
  { key: 'name', label: 'Name', defaultVisible: true },
  { key: 'slug', label: 'Slug', defaultVisible: true },
  { key: 'email', label: 'Email', defaultVisible: true },
  { key: 'status', label: 'Status', defaultVisible: true },
  { key: 'actions', label: 'Actions', defaultVisible: true },
];

export default function PartnersPage() {
  const { isAdmin } = useAuth();
  const { orderedDefs, moveColumn } = useColumnPrefs('soc.columns.partners', PARTNER_COLUMNS);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');

  async function load() {
    setLoading(true);
    const res = await axios.get('/api/partners');
    setPartners(res.data.data);
    setLoading(false);
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await axios.post('/api/partners', { name, slug, contactEmail: contactEmail || null });
      setName(''); setSlug(''); setContactEmail(''); setShowForm(false);
      load();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error while creating the partner');
    }
  }

  function startEdit(p: Partner) {
    setEditingId(p.id);
    setEditName(p.name);
    setEditEmail(p.contact_email || '');
  }

  async function saveEdit(id: number) {
    await axios.put(`/api/partners/${id}`, { name: editName, contactEmail: editEmail || null });
    setEditingId(null);
    load();
  }

  async function toggleActive(p: Partner) {
    await axios.put(`/api/partners/${p.id}`, { active: !p.active });
    load();
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-semibold text-gray-900">Partners (System Integrators)</h1>
        {isAdmin && (
<button onClick={() => setShowForm(v => !v)} className="btn-primary flex items-center gap-1">
          <Plus size={16} /> New partner
        </button>
)}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} className="card p-4 mb-4 flex flex-wrap gap-3 items-end">
          {error && <div className="w-full text-sm text-red-600">{error}</div>}
          <div>
            <label className="block text-xs text-gray-500 mb-1">Name</label>
            <input value={name} onChange={e => setName(e.target.value)} required className="input-field" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Slug</label>
            <input value={slug} onChange={e => setSlug(e.target.value)} required className="input-field" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Contact email</label>
            <input type="email" value={contactEmail} onChange={e => setContactEmail(e.target.value)} className="input-field" />
          </div>
          <button type="submit" className="btn-primary">Create</button>
        </form>
      )}

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr>
              {orderedDefs.map(d => (
                <DraggableTh key={d.key} colKey={d.key} onMove={moveColumn} className="table-header">
                  {d.label}
                </DraggableTh>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td className="table-cell text-gray-400" colSpan={5}>Loading…</td></tr>}
            {!loading && partners.length === 0 && (
              <tr><td className="table-cell text-gray-400" colSpan={5}>No partner created yet</td></tr>
            )}
            {partners.map(p => {
              const editing = editingId === p.id;
              return (
                <tr key={p.id} className="border-t border-gray-100">
                  {orderedDefs.map(d => (
                    <td key={d.key} className="table-cell">
                      {d.key === 'name' && (editing
                        ? <input value={editName} onChange={e => setEditName(e.target.value)} className="input-field py-1 text-xs w-40" />
                        : <span className="font-medium">{p.name}</span>)}
                      {d.key === 'slug' && <span className="text-gray-500">{p.slug}</span>}
                      {d.key === 'email' && (editing
                        ? <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} className="input-field py-1 text-xs w-52" />
                        : <span className="text-gray-500">{p.contact_email || '—'}</span>)}
                      {d.key === 'status' && (
                        <span className={`badge ${p.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {p.active ? 'Active' : 'Inactive'}
                        </span>
                      )}
                      {d.key === 'actions' && (editing ? (
                        <span className="inline-flex gap-1">
                          <button onClick={() => saveEdit(p.id)} className="p-1 text-green-600 hover:bg-green-50 rounded"><Check size={14} /></button>
                          <button onClick={() => setEditingId(null)} className="p-1 text-gray-400 hover:bg-gray-100 rounded"><X size={14} /></button>
                        </span>
                      ) : !isAdmin ? <span className="text-gray-300">—</span> : (
                        <span className="inline-flex gap-1">
                          <button onClick={() => startEdit(p)} title="Edit" className="p-1 text-gray-400 hover:text-primary-600 hover:bg-gray-50 rounded"><Pencil size={13} /></button>
                          <button onClick={() => toggleActive(p)} title={p.active ? 'Deactivate' : 'Reactivate'}
                            className="p-1 text-gray-400 hover:text-amber-600 hover:bg-gray-50 rounded">
                            {p.active ? <Ban size={13} /> : <CheckCircle2 size={13} />}
                          </button>
                        </span>
                      ))}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
